import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, ImageOff, Trash2, Save } from "lucide-react";

/**
 * Dialog che consente al SuperAdmin di configurare intestazione, logo, tagline
 * e contatti utilizzati nei PDF "Manuale d'Uso" e "Brochure Commerciale".
 */
export default function ManualSettingsDialog({ open, onOpenChange }) {
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    agency_name: "",
    tagline: "",
    contact_email: "",
    contact_phone: "",
    website: "",
    demo_url: "",
  });
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBust, setLogoBust] = useState(Date.now());
  const fileRef = useRef(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/superadmin/manual-settings");
      setForm({
        title: data.title || "",
        subtitle: data.subtitle || "",
        agency_name: data.agency_name || "",
        tagline: data.tagline || "",
        contact_email: data.contact_email || "",
        contact_phone: data.contact_phone || "",
        website: data.website || "",
        demo_url: data.demo_url || "",
      });
      setLogoUrl(data.logo_url || null);
      setLogoBust(Date.now());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento impostazioni");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/superadmin/manual-settings", form);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo troppo grande (max 2 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/superadmin/manual-settings/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.logo_url);
      setLogoBust(Date.now());
      toast.success("Logo caricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Rimuovere il logo del manuale?")) return;
    setUploading(true);
    try {
      await api.delete("/superadmin/manual-settings/logo");
      setLogoUrl(null);
      toast.success("Logo rimosso");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rimozione logo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="manual-settings-dialog"
      >
        <DialogHeader>
          <DialogTitle>Impostazioni Manuale &amp; Brochure</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Configura logo e intestazione utilizzati nei PDF generati dal SuperAdmin.
          </p>
        </DialogHeader>

        {busy ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Logo */}
            <div>
              <Label className="text-sm font-semibold">Logo intestazione</Label>
              <div className="mt-2 flex items-center gap-4">
                <div
                  className="h-24 w-24 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden"
                  data-testid="ms-logo-preview"
                >
                  {logoUrl ? (
                    <img
                      src={`${API.replace(/\/api$/, "")}${logoUrl}${logoUrl.includes("?") ? "&" : "?"}_=${logoBust}`}
                      alt="Logo manuale"
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <ImageOff className="h-8 w-8 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid="ms-upload-logo-btn"
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-1.5" />
                      )}
                      Carica logo
                    </Button>
                    {logoUrl && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={removeLogo}
                        disabled={uploading}
                        className="text-red-600 hover:bg-red-50"
                        data-testid="ms-remove-logo-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-1.5" />
                        Rimuovi
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500">
                    PNG · JPG · WEBP · SVG — max 2 MB. Se non impostato, viene usato il logo dell&apos;agenzia principale.
                  </p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={onFile}
                    data-testid="ms-logo-file-input"
                  />
                </div>
              </div>
            </div>

            {/* Intestazione */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm" htmlFor="ms-title">
                  Titolo (grande)
                </Label>
                <Input
                  id="ms-title"
                  data-testid="ms-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="es. GestAgePro"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Compare in copertina e nell&apos;intestazione di ogni pagina.
                </p>
              </div>
              <div>
                <Label className="text-sm" htmlFor="ms-subtitle">
                  Sottotitolo
                </Label>
                <Input
                  id="ms-subtitle"
                  data-testid="ms-subtitle-input"
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  placeholder="es. il Gestionale Agenzie Professionale"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Descrizione breve mostrata sotto il titolo.
                </p>
              </div>
            </div>

            <div>
              <Label className="text-sm" htmlFor="ms-tagline">
                Slogan (usato nella Brochure)
              </Label>
              <Textarea
                id="ms-tagline"
                data-testid="ms-tagline-input"
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder="es. La piattaforma completa per la tua agenzia assicurativa"
                rows={2}
                className="resize-none"
              />
            </div>

            <div>
              <Label className="text-sm" htmlFor="ms-agency">
                Nome agenzia (footer del Manuale)
              </Label>
              <Input
                id="ms-agency"
                data-testid="ms-agency-name-input"
                value={form.agency_name}
                onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
                placeholder="es. Studio di Consulenza Amoroso"
              />
            </div>

            <div className="border-t pt-4">
              <div className="text-sm font-semibold mb-2">Contatti mostrati nella Brochure</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs" htmlFor="ms-email">Email</Label>
                  <Input
                    id="ms-email"
                    data-testid="ms-contact-email-input"
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    placeholder="info@..."
                  />
                </div>
                <div>
                  <Label className="text-xs" htmlFor="ms-phone">Telefono</Label>
                  <Input
                    id="ms-phone"
                    data-testid="ms-contact-phone-input"
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    placeholder="+39 ..."
                  />
                </div>
                <div>
                  <Label className="text-xs" htmlFor="ms-web">Sito web</Label>
                  <Input
                    id="ms-web"
                    data-testid="ms-website-input"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="www...."
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                <span>QR Code richiesta demo (Brochure)</span>
                <span className="text-[10px] font-normal text-white bg-indigo-500 px-1.5 py-0.5 rounded">NEW</span>
              </div>
              <Label className="text-xs" htmlFor="ms-demo-url">URL modulo richiesta demo</Label>
              <Input
                id="ms-demo-url"
                data-testid="ms-demo-url-input"
                type="url"
                value={form.demo_url}
                onChange={(e) => setForm({ ...form, demo_url: e.target.value })}
                placeholder="https://www.tuodominio.it/richiedi-preventivo"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Nella brochure verrà stampato un <b>QR code</b> che punta a questo URL. Se lasciato vuoto, verrà usato automaticamente <code>APP_PUBLIC_URL/richiedi-preventivo</code>.
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="ms-cancel-btn"
          >
            Chiudi
          </Button>
          <Button
            onClick={save}
            disabled={saving || busy}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="ms-save-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1.5" />
            )}
            Salva impostazioni
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
